/*
重点練習 (90 秒) のお題選び。React も DOM も時計も乱数も触らない。

**ここは測定ではない。** 選んだ語は履歴に残るが、この回を標準測定として
保存しない (docs/typing/battle-measurement-spec.md)。苦手キーを集めた
90 秒は普通の文章より遅くなるので、履歴の速度に混ぜると「練習するほど
記録が下がる画面」になる。

乱数は引数で受け取る。中で Math.random を呼ぶと、同じ苦手キーの並びを
何度流しても違うお題が出て、検算ができなくなる。
*/

import { MIN_ATTEMPTS_FOR_WEAK_KEY, type WeakKey } from "./metrics";
import { PACES } from "./pace";
import { naturalRomaji, type RomajiSegments } from "./romaji-match";

/** 重点練習 1 回の長さ。討伐の 60 秒とも標準測定の 180 秒とも別の面 */
export const PRACTICE_DURATION_MS = 90_000;

/**
 * 0 以上 1 未満を返すもの。呼び出し側が持ってくる。
 *
 * 種を固定できる実装を渡せば、同じ苦手キーから同じお題の並びが再現できる
 */
export type PracticeRng = () => number;

/**
 * お題。ai-words.ts の AiWord と ai-phrases.ts の AiLine の共通部分だけ見る。
 *
 * meaning も japanese の中身もここでは読まない。単語でも句でも文でも
 * 同じ関数で選べるようにしておく
 */
export type PracticeWord = {
  japanese: string;
  hiragana: string;
};

/**
 * ひらがな 1 語を、打ち方の候補に分解するもの。
 *
 * **引数で受け取る。** 実体 (convertToRomajiPatterns) は TypingPage.tsx に
 * あって React を連れてくる。ここで import すると、お題を選ぶだけの計算が
 * 画面の都合で動かなくなる。battle.ts が乱数を引数で取っているのと同じ理由
 */
export type RomajiSegmentsOf = (hiragana: string) => RomajiSegments;

/*
苦手キーを含む語をどれだけ混ぜるか。

**1.0 にしない。** 苦手キーを含む語だけを並べると、材料が少ない人ほど
同じ語が何周も回る。そうなると練習ではなく暗記になって、覚えた並びを
打つ速さしか上がらない。

**0.5 でも足りない。** 半分が苦手と関係ない語だと、90 秒のうち 45 秒を
すでに打てるキーに使うことになる。重点練習と名前を付けた意味が消える。

0.7 は、10 語のうち 7 語が苦手キー入りという線。**実際に打って決めた
数ではない**ので、本文に埋め込まず名前でここに置く。手触りを見て動かす。
*/
export const WEAK_KEY_RATIO = 0.7;

/*
見積もった語数に掛ける余裕。

**足りないのと余るのは重さが違う。** 語を使い切ると画面が止まるが、
余った語は出さずに捨てるだけで何も起きない。非対称なので多い側に倒す。

1.5 は、見積もりの 1.5 倍まで速く打たれても持つ、という線
*/
export const PRACTICE_WORD_MARGIN = 1.5;

/** 速さが分からないときに当てる打鍵速度。一番速い相手の値を使う */
export const UNKNOWN_KPM = Math.max(...PACES.map((pace) => pace.kpm));

/**
 * 苦手キーとして扱うキーの集合。
 *
 * metrics.weakKeys はすでに MIN_ATTEMPTS_FOR_WEAK_KEY で絞っているが、
 * ここでも見る。生の集計をそのまま渡されたときに、1 回外しただけのキーで
 * 90 秒ぶんのお題が決まってしまうのを防ぐ。
 *
 * 小文字に寄せるのは、ローマ字の綴りが全部小文字で書いてあるため。
 * Shift を押しながら打った回を別のキーとして数えると、どの語にも当たらない
 */
export function practiceTargetKeys(weakKeys: readonly WeakKey[]): Set<string> {
  const keys = new Set<string>();
  for (const weak of weakKeys) {
    if (weak.attempts < MIN_ATTEMPTS_FOR_WEAK_KEY) continue;
    const key = weak.key.toLowerCase();
    if (key.length !== 1) continue;
    keys.add(key);
  }
  return keys;
}

/**
 * その語を打つときに押しうるキーの集合。
 *
 * **ひらがなで照合しない。** 苦手なのはローマ字のキーであって、かなでは
 * ない。「し」は si でも shi でも ci でも打てるので、s の人にも h の人にも
 * c の人にも当たる語になる。どれか 1 つの綴りだけを見ると、si で打つ人に
 * h の練習を出し続けることになる。
 *
 * 候補の**和**を取る。打ち方は本人が選ぶので、こちらからは絞れない
 */
export function practiceKeysOf(
  hiragana: string,
  segmentsOf: RomajiSegmentsOf,
): Set<string> {
  const keys = new Set<string>();
  for (const segment of segmentsOf(hiragana)) {
    for (const option of segment) {
      for (const char of option) keys.add(char.toLowerCase());
    }
  }
  return keys;
}

/** その語が苦手キーのどれかを打たせるか */
export function coversWeakKey(
  hiragana: string,
  targetKeys: ReadonlySet<string>,
  segmentsOf: RomajiSegmentsOf,
): boolean {
  if (targetKeys.size === 0) return false;
  for (const key of practiceKeysOf(hiragana, segmentsOf)) {
    if (targetKeys.has(key)) return true;
  }
  return false;
}

/** その語が当てている苦手キーの数。多いほど 1 語で多く練習できる */
export function weakKeyHits(
  hiragana: string,
  targetKeys: ReadonlySet<string>,
  segmentsOf: RomajiSegmentsOf,
): number {
  if (targetKeys.size === 0) return 0;
  let hits = 0;
  for (const key of practiceKeysOf(hiragana, segmentsOf)) {
    if (targetKeys.has(key)) hits++;
  }
  return hits;
}

/**
 * 1 語あたりの打鍵数。いちばん自然な綴りの長さで数える。
 *
 * 一番短い綴りで数えると、全員が最短で打つ前提の見積もりになって
 * 語が足りなくなる。画面のヒントに出るのと同じ綴り (naturalRomaji) で見る
 */
export function keystrokesOf(
  hiragana: string,
  segmentsOf: RomajiSegmentsOf,
): number {
  return naturalRomaji(segmentsOf(hiragana)).length;
}

/** 渡された語の平均打鍵数。0 語なら 0 */
export function averageKeystrokes(
  words: readonly PracticeWord[],
  segmentsOf: RomajiSegmentsOf,
): number {
  if (words.length === 0) return 0;
  let total = 0;
  for (const word of words) total += keystrokesOf(word.hiragana, segmentsOf);
  return total / words.length;
}

/*
90 秒ぶんの語数。

**数を埋め込まない。** 「90 秒なら 30 語」と書くと、かめ (120 KPM) の人は
半分も打てずに終わり、ふくろう (340 KPM) の人は 40 秒で語を使い切る。
同じ 1 つの数がどちらにも合う場面が無い。

    打鍵数 = kpm × (時間 / 60 秒)
    語数   = 打鍵数 / 1 語あたりの打鍵数 × 余裕

1 語あたりの打鍵数は語そのものから測る (averageKeystrokes)。お題を
足したり文に変えたりしたときに、この関数を直さなくて済む。

速さが分からないときは一番速い相手の値を当てる。余った語は捨てるだけだが、
足りないと画面が止まる
*/
export function practiceWordCount(
  kpm: number,
  keystrokesPerWord: number,
  durationMs: number = PRACTICE_DURATION_MS,
): number {
  if (!Number.isFinite(keystrokesPerWord) || keystrokesPerWord <= 0) return 0;
  if (!Number.isFinite(durationMs) || durationMs <= 0) return 0;
  const speed = Number.isFinite(kpm) && kpm > 0 ? kpm : UNKNOWN_KPM;
  const keystrokes = (speed * durationMs) / 60_000;
  const count = (keystrokes / keystrokesPerWord) * PRACTICE_WORD_MARGIN;
  return Math.max(1, Math.ceil(count));
}

export type PracticeOptions = {
  /** 出す語の数。practiceWordCount で出したものを渡す */
  count: number;
  /** ひらがなを打ち方の候補に分解するもの */
  segmentsOf: RomajiSegmentsOf;
  /** 苦手キーを含む語の割合。省略時は WEAK_KEY_RATIO */
  weakKeyRatio?: number;
};

/**
 * 重点練習で出す語を選ぶ。
 *
 * 苦手キーを含む語を weakKeyRatio の割合で混ぜる。
 *
 * **直前と同じ語は出さない。** 同じ語が続くと、読む手間が消えて指の運びを
 * なぞるだけになる。他に選べる語が 1 つも無いときだけ続けて出す。
 *
 * **苦手キーが 1 つも無くても空を返さない。** 初回や MIN_ATTEMPTS に
 * 届いていない人はここを通る。普通の出題 (全語から一様に選ぶ) に落ちる
 */
export function selectPracticeWords<T extends PracticeWord>(
  words: readonly T[],
  weakKeys: readonly WeakKey[],
  options: PracticeOptions,
  rng: PracticeRng,
): T[] {
  const count = Math.max(0, Math.floor(options.count));
  if (words.length === 0 || count === 0) return [];

  const ratio = options.weakKeyRatio ?? WEAK_KEY_RATIO;
  const targetKeys = practiceTargetKeys(weakKeys);

  const weak: T[] = [];
  const plain: T[] = [];
  for (const word of words) {
    if (coversWeakKey(word.hiragana, targetKeys, options.segmentsOf)) {
      weak.push(word);
    } else {
      plain.push(word);
    }
  }

  const picked: T[] = [];
  let previous: string | undefined;
  for (let i = 0; i < count; i++) {
    // 引く数は必ず先に取る。片方が空のときだけ引かない形にすると、
    // 苦手キーの有無で乱数の並びがずれて、種を固定しても再現できなくなる
    const wantWeak = rng() < ratio;
    const primary = wantWeak ? weak : plain;
    const fallback = wantWeak ? plain : weak;
    const pool = primary.length > 0 ? primary : fallback;

    const fresh = pool.filter((word) => word.hiragana !== previous);
    const from = fresh.length > 0 ? fresh : pool;
    // rng() が 1 を返す実装を渡されても範囲から出ないようにする
    const index = Math.min(from.length - 1, Math.floor(rng() * from.length));
    const word = from[index];
    picked.push(word);
    previous = word.hiragana;
  }
  return picked;
}
